import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CHeader, CInput, CButton } from '../../components/common';
import { createStockCall } from '../../api';
import routes from '../../utils/routes';

const HORIZON_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '15 days', value: 15 },
  { label: '30 days', value: 30 },
  { label: '60 days', value: 60 },
  { label: '90 days', value: 90 },
];

const NewStockCall: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);

  const [tickerSymbol, setTickerSymbol] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [exchange, setExchange] = useState<'NSE' | 'BSE'>('NSE');
  const [callType, setCallType] = useState<'BUY' | 'SELL'>('BUY');
  const [entryPrice, setEntryPrice] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [stopLossPrice, setStopLossPrice] = useState('');
  const [timeHorizon, setTimeHorizon] = useState(30);
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validate = (): string | null => {
    if (!tickerSymbol.trim()) return 'Ticker symbol is required';
    if (!companyName.trim()) return 'Company name is required';
    const entry = parseFloat(entryPrice);
    const target = parseFloat(targetPrice);
    const sl = parseFloat(stopLossPrice);
    if (isNaN(entry) || entry <= 0) return 'Valid entry price required';
    if (isNaN(target) || target <= 0) return 'Valid target price required';
    if (isNaN(sl) || sl <= 0) return 'Valid stop loss price required';
    if (callType === 'BUY') {
      if (target <= entry) return 'BUY: Target must be above entry price';
      if (sl >= entry) return 'BUY: Stop loss must be below entry price';
    } else {
      if (target >= entry) return 'SELL: Target must be below entry price';
      if (sl <= entry) return 'SELL: Stop loss must be above entry price';
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await createStockCall({
        ticker_symbol: tickerSymbol.toUpperCase().trim(),
        company_name: companyName.trim(),
        exchange,
        call_type: callType,
        entry_price: parseFloat(entryPrice),
        target_price: parseFloat(targetPrice),
        stop_loss_price: parseFloat(stopLossPrice),
        time_horizon_days: timeHorizon,
        rationale: rationale.trim() || undefined,
      });
      navigate(routes.expertCalls);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create call');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: colors.backgroundColor }}>
      <div className="p-4 lg:p-8">
        <div className="lg:max-w-xl lg:mx-auto">
          <CHeader title="Log New Call" />

          <div className="space-y-5 mt-4">
            {/* Ticker + Company */}
            <div>
              <CText type="M12" color={colors.grayScale500} className="mb-1">Ticker Symbol</CText>
              <CInput
                value={tickerSymbol}
                onChange={setTickerSymbol}
                placeholder="e.g. HDFCBANK"
              />
            </div>

            <div>
              <CText type="M12" color={colors.grayScale500} className="mb-1">Company Name</CText>
              <CInput
                value={companyName}
                onChange={setCompanyName}
                placeholder="e.g. HDFC Bank Ltd"
              />
            </div>

            {/* Exchange Toggle */}
            <div>
              <CText type="M12" color={colors.grayScale500} className="mb-2">Exchange</CText>
              <div className="flex gap-2">
                {(['NSE', 'BSE'] as const).map(ex => (
                  <button
                    key={ex}
                    onClick={() => setExchange(ex)}
                    className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all"
                    style={{
                      backgroundColor: exchange === ex ? colors.primary : colors.inputBackground,
                      color: exchange === ex ? '#fff' : colors.textColor,
                      border: `1px solid ${exchange === ex ? colors.primary : colors.borderColor}`,
                    }}
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>

            {/* BUY/SELL Toggle */}
            <div>
              <CText type="M12" color={colors.grayScale500} className="mb-2">Call Type</CText>
              <div className="flex gap-2">
                {(['BUY', 'SELL'] as const).map(ct => (
                  <button
                    key={ct}
                    onClick={() => setCallType(ct)}
                    className="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all"
                    style={{
                      backgroundColor: callType === ct
                        ? ct === 'BUY' ? '#22c55e' : '#ef4444'
                        : colors.inputBackground,
                      color: callType === ct ? '#fff' : colors.textColor,
                      border: `1px solid ${callType === ct
                        ? ct === 'BUY' ? '#22c55e' : '#ef4444'
                        : colors.borderColor}`,
                    }}
                  >
                    {ct}
                  </button>
                ))}
              </div>
            </div>

            {/* Prices */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <CText type="M12" color={colors.grayScale500} className="mb-1">Entry Price</CText>
                <CInput
                  value={entryPrice}
                  onChange={setEntryPrice}
                  placeholder="0.00"
                  type="number"
                />
              </div>
              <div>
                <CText type="M12" color={colors.grayScale500} className="mb-1">Target Price</CText>
                <CInput
                  value={targetPrice}
                  onChange={setTargetPrice}
                  placeholder="0.00"
                  type="number"
                />
              </div>
              <div>
                <CText type="M12" color={colors.grayScale500} className="mb-1">Stop Loss</CText>
                <CInput
                  value={stopLossPrice}
                  onChange={setStopLossPrice}
                  placeholder="0.00"
                  type="number"
                />
              </div>
            </div>

            {/* Potential return preview */}
            {entryPrice && targetPrice && parseFloat(entryPrice) > 0 && (
              <div className="rounded-xl p-3" style={{ backgroundColor: colors.inputBackground }}>
                <CText type="r12" color={colors.grayScale500}>
                  Potential return:{' '}
                  <span style={{ color: '#22c55e', fontWeight: 600 }}>
                    {callType === 'BUY'
                      ? (((parseFloat(targetPrice) - parseFloat(entryPrice)) / parseFloat(entryPrice)) * 100).toFixed(1)
                      : (((parseFloat(entryPrice) - parseFloat(targetPrice)) / parseFloat(entryPrice)) * 100).toFixed(1)
                    }%
                  </span>
                  {stopLossPrice && parseFloat(stopLossPrice) > 0 && (
                    <>
                      {' | Risk: '}
                      <span style={{ color: '#ef4444', fontWeight: 600 }}>
                        {callType === 'BUY'
                          ? (((parseFloat(entryPrice) - parseFloat(stopLossPrice)) / parseFloat(entryPrice)) * 100).toFixed(1)
                          : (((parseFloat(stopLossPrice) - parseFloat(entryPrice)) / parseFloat(entryPrice)) * 100).toFixed(1)
                        }%
                      </span>
                    </>
                  )}
                </CText>
              </div>
            )}

            {/* Time Horizon */}
            <div>
              <CText type="M12" color={colors.grayScale500} className="mb-2">Time Horizon</CText>
              <div className="flex flex-wrap gap-2">
                {HORIZON_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setTimeHorizon(opt.value)}
                    className="px-4 py-2 rounded-full text-sm font-medium transition-all"
                    style={{
                      backgroundColor: timeHorizon === opt.value ? colors.primary : 'transparent',
                      color: timeHorizon === opt.value ? '#fff' : colors.textColor,
                      border: `1px solid ${timeHorizon === opt.value ? colors.primary : colors.borderColor}`,
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Rationale */}
            <div>
              <CText type="M12" color={colors.grayScale500} className="mb-1">Rationale (optional)</CText>
              <textarea
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
                placeholder="Why are you making this call?"
                rows={3}
                className="w-full rounded-xl p-3 text-sm resize-none outline-none"
                style={{
                  backgroundColor: colors.inputBackground,
                  color: colors.textColor,
                  border: `1px solid ${colors.borderColor}`,
                }}
              />
            </div>

            {/* Error */}
            {error && (
              <div className="rounded-xl p-3" style={{ backgroundColor: '#ef444415' }}>
                <CText type="r12" color="#ef4444">{error}</CText>
              </div>
            )}

            {/* Submit */}
            <CButton
              title={submitting ? 'Submitting...' : 'Log Call'}
              onPress={handleSubmit}
              disabled={submitting}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewStockCall;
